import type {FC} from 'react';
import clsx from 'clsx';

import {Badge} from '@/modules/finkn/components/badge';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {createPhoneLink, formatPhoneNumber} from '@/modules/foundation/shared/format';

import type {Person} from '../../shared/types';

export interface PersonCardProps {
	person: Person;
}

export const PersonCard: FC<PersonCardProps> = ({person}) => {
	const {name, image, phone, email, role, company, departmentManager} = person;

	return (
		<div
			className={clsx(
				'relative',
				'p-6',
				'border',
				'border-grey-border',
				'rounded-lg',
				'inline-flex',
				'gap-4',
				'h-full',
			)}
		>
			{image && (
				<div
					className={clsx(
						'size-[66px]',
						'rounded-full',
						'overflow-hidden',
						'border-2',
						'border-warning-35',
						'shrink-0',
					)}
				>
					<ImageBlock
						alt={image.altText ?? ''}
						height={image.image.height}
						width={image.image.width}
						src={image.image.src}
					/>
				</div>
			)}
			<div>
				<Heading size="xs" spacing asChild>
					<p className={clsx('flex', 'flex-wrap', 'gap-2', 'items-center')}>
						{name} {departmentManager && <Badge variant="outline">Avd. leder</Badge>}
					</p>
				</Heading>

				<div>
					{role && (
						<Paragraph
							size="sm"
							className={clsx((phone || email) && !company && 'mb-2')}
						>
							{role}
						</Paragraph>
					)}

					{company && (
						<Paragraph size="xs" className={clsx('italic', (phone || email) && 'mb-2')}>
							{company}
						</Paragraph>
					)}

					{phone && (
						<Paragraph size="sm" className={clsx('text-grey-15')}>
							<strong>Tlf:</strong>{' '}
							<a href={createPhoneLink(phone)}>{formatPhoneNumber(phone)}</a>
						</Paragraph>
					)}

					{email && (
						<Paragraph size="sm" className={clsx('text-grey-15')}>
							<strong>Epost:</strong> <a href={`mailto:${email}`}>{email}</a>
						</Paragraph>
					)}
				</div>
			</div>
		</div>
	);
};
